import { Injectable, NgZone } from '@angular/core';
import { BehaviorSubject } from 'rxjs';
import type { FirebaseApp } from 'firebase/app';
import type { Auth, User } from 'firebase/auth';
import type { Firestore } from 'firebase/firestore';
import { firebaseConfig } from '../../environments/firebase';
import { environment } from '../../environments/environment';

@Injectable({ providedIn: 'root' })
export class AuthService {
  private app!: FirebaseApp;
  private auth!: Auth;
  private db!: Firestore;

  private userSubject = new BehaviorSubject<User | null>(null);
  private allowedSubject = new BehaviorSubject<boolean>(false);
  // 'all' = full software access; string[] = specific tool keys; [] = no access
  private allowedToolsSubject = new BehaviorSubject<string[] | 'all'>([]);
  // Per-user grants for admin-built tabs/features (weeklyJournal, etc.) — see hasFeature().
  private allowedFeaturesSubject = new BehaviorSubject<string[]>([]);
  private loadingSubject = new BehaviorSubject<boolean>(true);
  private adminSubject = new BehaviorSubject<boolean>(false);
  private employeeSubject = new BehaviorSubject<boolean>(false);

  user$ = this.userSubject.asObservable();
  isAllowed$ = this.allowedSubject.asObservable();
  allowedTools$ = this.allowedToolsSubject.asObservable();
  allowedFeatures$ = this.allowedFeaturesSubject.asObservable();
  loading$ = this.loadingSubject.asObservable();
  isAdmin$ = this.adminSubject.asObservable();
  isEmployee$ = this.employeeSubject.asObservable();
  private initPromise?: Promise<void>;
  private firestorePromise?: Promise<void>;
  private authApi?: typeof import('firebase/auth');
  private firestoreApi?: typeof import('firebase/firestore');
  private softwareAccessUnsub?: () => void;
  private featureAccessUnsub?: () => void;

  constructor(private zone: NgZone) {}

  startAfterInitialRender(): void {
    if (this.initPromise) {
      return;
    }

    const start = () => { void this.initAuth(); };
    const idleWindow = window as Window & { requestIdleCallback?: (callback: () => void, options?: { timeout: number }) => number };

    if (typeof idleWindow.requestIdleCallback === 'function') {
      idleWindow.requestIdleCallback(start, { timeout: 3000 });
      return;
    }

    window.setTimeout(start, 1500);
  }

  ensureReady(): Promise<void> {
    return this.initAuth();
  }

  private initAuth(): Promise<void> {
    if (!this.initPromise) {
      this.initPromise = this.loadFirebase();
    }
    return this.initPromise;
  }

  private async loadFirebase(): Promise<void> {
    const [appApi, authApi] = await Promise.all([
      import('firebase/app'),
      import('firebase/auth'),
    ]);

    this.authApi = authApi;

    this.app = appApi.getApps().length ? appApi.getApps()[0] : appApi.initializeApp(firebaseConfig);
    this.auth = authApi.getAuth(this.app);

    authApi.onAuthStateChanged(this.auth, (user) => {
      void this.zone.run(async () => {
        this.loadingSubject.next(true);
        try {
          this.softwareAccessUnsub?.();
          this.softwareAccessUnsub = undefined;
          this.featureAccessUnsub?.();
          this.featureAccessUnsub = undefined;

          this.userSubject.next(user);
          if (user) {
            await this.ensureFirestoreReady();
            await this.saveUserIfNew(user).catch(() => undefined);
            await this.bootstrapEmployeeAccess(user).catch(() => undefined);
            const [allowed, allowedTools, allowedFeatures, admin, employee] = await Promise.all([
              this.checkAllowed(user.uid).catch(() => false),
              this.checkSoftwareAllowed(user.uid).catch(() => [] as string[]),
              this.checkFeatureAllowed(user.uid).catch(() => [] as string[]),
              this.checkAdmin(user).catch(() => false),
              this.checkEmployee(user.uid).catch(() => false),
            ]);
            this.allowedSubject.next(allowed);
            this.allowedToolsSubject.next(allowedTools);
            this.allowedFeaturesSubject.next(allowedFeatures);
            this.adminSubject.next(admin);
            this.employeeSubject.next(employee);
            this.watchSoftwareAccess(user.uid);
            this.watchFeatureAccess(user.uid);
            void this.claimPendingPurchase(user);

            // Block login for contract employees whose contract ended >1 month ago
            if (employee && !admin && user.email) {
              const revoked = await this.checkContractRevoked(user.email).catch(() => false);
              if (revoked) {
                sessionStorage.setItem(
                  'propdesk_access_revoked',
                  'Your access has been deactivated — your contract ended more than 30 days ago. Please contact your manager.'
                );
                await this.requireAuthApi().signOut(this.auth);
                return;
              }
            }
          } else {
            this.allowedSubject.next(false);
            this.allowedToolsSubject.next([]);
            this.allowedFeaturesSubject.next([]);
            this.adminSubject.next(false);
            this.employeeSubject.next(false);
          }
        } finally {
          this.loadingSubject.next(false);
        }
      });
    });
  }

  private async ensureFirestoreReady(): Promise<void> {
    if (!this.firestorePromise) {
      this.firestorePromise = this.loadFirestore();
    }
    return this.firestorePromise;
  }

  private async loadFirestore(): Promise<void> {
    const firestoreApi = await import('firebase/firestore');

    this.firestoreApi = firestoreApi;

    if (environment.appCheckEnabled) {
      const appCheckApi = await import('firebase/app-check');
      try {
        appCheckApi.initializeAppCheck(this.app, {
          provider: new appCheckApi.ReCaptchaV3Provider(environment.recaptchaSiteKey),
          isTokenAutoRefreshEnabled: true,
        });
      } catch {
        // App Check may already be initialized by another lazy Firebase service.
      }
    }

    try {
      this.db = firestoreApi.initializeFirestore(this.app, {
        localCache: firestoreApi.persistentLocalCache({
          tabManager: firestoreApi.persistentMultipleTabManager(),
        }),
      });
    } catch {
      this.db = firestoreApi.getFirestore(this.app);
    }
  }

  get currentUser(): User | null {
    return this.userSubject.value;
  }

  get isAllowed(): boolean {
    return this.allowedSubject.value;
  }

  get isLoading(): boolean {
    return this.loadingSubject.value;
  }

  isToolAllowed(key: string): boolean {
    const tools = this.allowedToolsSubject.value;
    if (tools === 'all') return true;
    return tools.includes(key);
  }

  /**
   * Per-user access to an admin-built tab/feature (e.g. 'weekly-journal'),
   * granted by adding the key to allowedFeatureUsers/{uid}.features in
   * Firestore. Admins always pass, same as canAccessLevelData does for
   * isAdmin — callers don't need to also check auth.isAdmin.
   */
  hasFeature(key: string): boolean {
    return this.isAdmin || this.allowedFeaturesSubject.value.includes(key);
  }

  // True for a customer who bought at least one tool via Stripe (allowedSoftwareUsers),
  // independent of isAllowed (the separate, manually-granted full-site allowedUsers gate).
  get hasSoftwareAccess(): boolean {
    const tools = this.allowedToolsSubject.value;
    return tools === 'all' || tools.length > 0;
  }

  // Anyone with *some* standing on the site: admin, manually-approved (isAllowed), or a
  // Stripe tool purchaser. Use this instead of isAllowed alone for any "are they a member"
  // gate (members-only content, login landing) — isAllowed alone misses the other two.
  get isMember(): boolean {
    return this.isAdmin || this.isAllowed || this.hasSoftwareAccess;
  }

  /**
   * Mirrors the Firestore rules' canAccessLevelMapPro(): admin, a full
   * allowedUsers member, or anyone who owns the Level Map Pro Indicator.
   */
  get canAccessLevelData(): boolean {
    return this.isAdmin || this.isAllowed || this.isToolAllowed('level-map-pro');
  }

  async signInWithGoogle(): Promise<void> {
    await this.ensureReady();
    const authApi = this.requireAuthApi();
    const provider = new authApi.GoogleAuthProvider();
    await authApi.signInWithPopup(this.auth, provider);
  }

  async signOut(): Promise<void> {
    await this.ensureReady();
    await this.requireAuthApi().signOut(this.auth);
  }

  private async saveUserIfNew(user: User): Promise<void> {
    const firestoreApi = this.requireFirestoreApi();
    const userRef = firestoreApi.doc(this.db, 'users', user.uid);
    const snap = await firestoreApi.getDoc(userRef);
    if (!snap.exists()) {
      await firestoreApi.setDoc(userRef, {
        email: user.email,
        displayName: user.displayName,
        photoURL: user.photoURL,
        createdAt: firestoreApi.serverTimestamp(),
      });
    }
  }

  get isAdmin(): boolean {
    return this.adminSubject.value;
  }

  get isEmployee(): boolean {
    return this.employeeSubject.value;
  }

  private async bootstrapEmployeeAccess(user: User): Promise<void> {
    if (!user.email) return;
    const api = this.requireFirestoreApi();

    // Already linked — nothing to do
    const empRef = api.doc(this.db, 'employees', user.uid);
    const empSnap = await api.getDoc(empRef);
    if (empSnap.exists()) return;

    // Look for a matching profile by email
    const q = api.query(
      api.collection(this.db, 'employeeProfiles'),
      api.where('email', '==', user.email)
    );
    const snap = await api.getDocs(q);
    if (snap.empty) return;

    const profileDoc = snap.docs[0];

    // Link UID → profile (creates employees/{uid})
    await api.setDoc(empRef, {
      email: user.email,
      profileId: profileDoc.id,
      linkedAt: api.serverTimestamp(),
    });

    // Write UID back onto the profile so admin can see it
    await api.setDoc(profileDoc.ref, { linkedUid: user.uid }, { merge: true });
  }

  private async checkEmployee(uid: string): Promise<boolean> {
    const firestoreApi = this.requireFirestoreApi();
    const ref = firestoreApi.doc(this.db, 'employees', uid);
    const snap = await firestoreApi.getDoc(ref);
    return snap.exists();
  }

  private async checkAdmin(user: User): Promise<boolean> {
    // Primary: JWT custom claim — server-signed by Firebase, cannot be spoofed in DevTools
    try {
      const tokenResult = await user.getIdTokenResult();
      if (tokenResult.claims['admin'] === true) return true;
    } catch {
      // fall through to Firestore fallback
    }

    // Fallback: Firestore check (existing behaviour — keeps working until claim is set)
    const firestoreApi = this.requireFirestoreApi();
    const ref = firestoreApi.doc(this.db, 'admins', user.uid);
    const snap = await firestoreApi.getDoc(ref);
    return snap.exists();
  }

  private async checkAllowed(uid: string): Promise<boolean> {
    const firestoreApi = this.requireFirestoreApi();
    const ref = firestoreApi.doc(this.db, 'allowedUsers', uid);
    const snap = await firestoreApi.getDoc(ref);
    return snap.exists();
  }

  /**
   * Returns 'all' if the user has full software access (doc exists, no tools field),
   * or a string[] of specific tool keys if restricted, or [] if not in collection.
   *
   * Firestore doc format:
   *   Full access:     allowedSoftwareUsers/{uid}  ->  { } (no tools field)
   *   Restricted:      allowedSoftwareUsers/{uid}  ->  { tools: ['levels', 'level-map-pro'] }
   */
  private async checkSoftwareAllowed(uid: string): Promise<string[] | 'all'> {
    const firestoreApi = this.requireFirestoreApi();
    const ref = firestoreApi.doc(this.db, 'allowedSoftwareUsers', uid);
    const snap = await firestoreApi.getDoc(ref);
    if (!snap.exists()) return [];
    const data = snap.data();
    if (!data?.['tools']) return 'all';
    return data['tools'] as string[];
  }

  /**
   * Keeps allowedToolsSubject live so a Stripe purchase (which writes
   * allowedSoftwareUsers/{uid} from the webhook, not from this browser tab)
   * unlocks access without the user having to reload the page.
   */
  private watchSoftwareAccess(uid: string): void {
    const firestoreApi = this.requireFirestoreApi();
    const ref = firestoreApi.doc(this.db, 'allowedSoftwareUsers', uid);
    this.softwareAccessUnsub = firestoreApi.onSnapshot(
      ref,
      (snap) => {
        this.zone.run(() => {
          if (!snap.exists()) {
            this.allowedToolsSubject.next([]);
            return;
          }
          const data = snap.data();
          this.allowedToolsSubject.next(!data?.['tools'] ? 'all' : (data['tools'] as string[]));
        });
      },
      () => {
        // Ignore transient permission/offline errors — the last known value stands.
      }
    );
  }

  /**
   * allowedFeatureUsers/{uid} -> { features: string[] } grants a user
   * visibility into specific admin-built tabs (e.g. the AlgoDesk "Journal"
   * tab) without making them a full admin. Add/remove keys from the array
   * in Firestore to grant/revoke a tab per user.
   */
  private async checkFeatureAllowed(uid: string): Promise<string[]> {
    const firestoreApi = this.requireFirestoreApi();
    const ref = firestoreApi.doc(this.db, 'allowedFeatureUsers', uid);
    const snap = await firestoreApi.getDoc(ref);
    if (!snap.exists()) return [];
    return (snap.data()?.['features'] as string[] | undefined) ?? [];
  }

  private watchFeatureAccess(uid: string): void {
    const firestoreApi = this.requireFirestoreApi();
    const ref = firestoreApi.doc(this.db, 'allowedFeatureUsers', uid);
    this.featureAccessUnsub = firestoreApi.onSnapshot(
      ref,
      (snap) => {
        this.zone.run(() => {
          if (!snap.exists()) {
            this.allowedFeaturesSubject.next([]);
            return;
          }
          this.allowedFeaturesSubject.next((snap.data()?.['features'] as string[] | undefined) ?? []);
        });
      },
      () => {
        // Ignore transient permission/offline errors — the last known value stands.
      }
    );
  }

  /**
   * Redeems a Stripe purchase made before this user signed in (parked under
   * their checkout email by the webhook) onto their account. Safe to call on
   * every sign-in — it's a no-op when nothing is pending. The subsequent
   * write to allowedSoftwareUsers/{uid} is picked up automatically by the
   * live watchSoftwareAccess listener.
   */
  private async claimPendingPurchase(user: User): Promise<void> {
    try {
      const idToken = await user.getIdToken();
      const res = await fetch('/api/claim-purchase', {
        method: 'POST',
        headers: { Authorization: `Bearer ${idToken}` },
      });
      // Full-site access granted ahead of sign-up is read once at login, so switch it on here rather than needing a reload.
      const body = await res.json().catch(() => ({}));
      if (body?.fullAccess) this.allowedSubject.next(true);
    } catch {
      // Best-effort — a failed claim attempt isn't fatal; nothing was granted.
    }
  }

  private async checkContractRevoked(email: string): Promise<boolean> {
    const api = this.requireFirestoreApi();
    const q = api.query(
      api.collection(this.db, 'employeeProfiles'),
      api.where('email', '==', email.toLowerCase())
    );
    const snap = await api.getDocs(q);
    if (snap.empty) return false;
    const data = snap.docs[0].data();
    // Any employee with an end date (contract expiry OR indeterminate termination)
    const endDate = data['contractEndDate'] as string;
    if (!endDate) return false;
    const oneMonthAfter = new Date(endDate + 'T12:00:00');
    oneMonthAfter.setMonth(oneMonthAfter.getMonth() + 1);
    return new Date() > oneMonthAfter;
  }

  private requireAuthApi(): typeof import('firebase/auth') {
    if (!this.authApi) throw new Error('Firebase Auth is not ready.');
    return this.authApi;
  }

  private requireFirestoreApi(): typeof import('firebase/firestore') {
    if (!this.firestoreApi) throw new Error('Firestore is not ready.');
    return this.firestoreApi;
  }
}


